/**
 * Money, as an exact decimal string.
 *
 * Same treatment as `Site.acreage` and estimated labor hours, and for the
 * sharpest version of the same reason: a rate is multiplied by hours to produce
 * a quote, and a rate that is nearly 85 is how a quote ends up nearly right.
 * Dollars are carried as `"85.00"` end to end and never pass through a JS
 * number on the way to the database.
 *
 * Currency is not modeled. Smithwell is one US business, which is an
 * assumption worth naming rather than a detail worth carrying on every row.
 */

/** Cents, so all arithmetic happens in integers. */
const CENTS = 100

/**
 * A dollar amount on the wire: up to eight digits, at most two decimal places.
 *
 * `"85"` and `"85.00"` are both amounts. The pattern is the one `parseMoney`
 * accepts, so a schema and the parser cannot disagree about what money is.
 */
export const MONEY_AMOUNT_PATTERN = /^\d{1,8}(\.\d{1,2})?$/

/**
 * Parse a dollar amount into integer cents, or null if it is not one.
 *
 * Strict rather than `Number.parseFloat`, which accepts `"85abc"` and
 * `"Infinity"`. A value that does not round-trip is a bug upstream, and
 * coercing it quietly would put a wrong number into a price.
 */
export function parseMoney(value: string | null): number | null {
  if (value === null) return null
  const trimmed = value.trim()
  if (!MONEY_AMOUNT_PATTERN.test(trimmed)) return null
  return Math.round(Number(trimmed) * CENTS)
}

/**
 * The wire format: two decimal places, always.
 *
 * Matches the column's scale, so a value read back is byte-identical to the one
 * written — the same contract `formatHoursValue` keeps for hours.
 */
export function formatMoneyValue(cents: number): string {
  return (cents / CENTS).toFixed(2)
}

/**
 * Money as it reads to a client: `"$1,234.50"`.
 *
 * Grouped and always to the cent. Unlike hours, trailing zeros stay — a price
 * written `"$1,234.5"` looks like a typo in a document somebody is deciding
 * money against.
 */
export function formatMoney(cents: number): string {
  const negative = cents < 0
  const whole = Math.floor(Math.abs(cents) / CENTS)
  const remainder = Math.abs(cents) % CENTS
  const grouped = whole.toLocaleString('en-US')
  const decimals = remainder.toString().padStart(2, '0')
  return `${negative ? '-' : ''}$${grouped}.${decimals}`
}

/**
 * A stored amount (`"1234.50"`) as a reader sees it (`"$1,234.50"`).
 *
 * Every dollar figure on a page goes through here. A header that prints the
 * wire string with a `$` glued on, and a table that formats cents, will
 * disagree about the comma the moment a total crosses a thousand. Null, or a
 * value that is not money, reads as an em dash — the same mark an empty cell
 * uses.
 */
export function formatMoneyAmount(value: string | null): string {
  const cents = parseMoney(value)
  return cents === null ? '—' : formatMoney(cents)
}

/**
 * Price a quantity of minutes at an hourly rate.
 *
 * Integer arithmetic all the way through, rounded once at the end. Pricing each
 * tree and summing would round ninety times and land somewhere near the answer;
 * this rounds the site's total exactly once, which is the number a board will
 * check by hand.
 */
export function priceMinutes(minutes: number, centsPerHour: number): number {
  return Math.round((minutes * centsPerHour) / 60)
}

/**
 * Money with the cents dropped when there are none: `"$2,000,000"`.
 *
 * For figures that are round by nature — an insurance limit, a coverage
 * aggregate — where ".00" is three characters of noise repeated down a column.
 * NOT for prices: a quote written "$1,234" instead of "$1,234.00" looks
 * rounded, and a client is entitled to see the exact figure they are agreeing
 * to. Cents still print when a value actually has them, so nothing is hidden.
 */
export function formatMoneyWhole(cents: number): string {
  return cents % CENTS === 0
    ? formatMoney(cents).replace(/\.00$/, '')
    : formatMoney(cents)
}
